<script setup>
import { computed } from 'vue';
import { HeatmapChart } from '@chatwoot/viz';

import { groupHeatmapByDay } from 'helpers/ReportsDataHelper';
import { useI18n } from 'vue-i18n';
import {
  formatHeatmapRowLabel,
  formatHeatmapRowDescription,
} from './heatmapLabels';

const props = defineProps({
  heatmapData: {
    type: Array,
    default: () => [],
  },
  numberOfRows: {
    type: Number,
    default: 7,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  colorScheme: {
    type: String,
    default: 'blue',
    validator: value => ['blue', 'green'].includes(value),
  },
  ariaLabel: {
    type: String,
    required: true,
  },
  formatValue: {
    type: Function,
    required: true,
  },
});
const { t, locale } = useI18n();

const ROW_LABEL_WIDTH = 84;
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

const columns = computed(() =>
  HOURS.map(hour => ({
    id: hour,
    label: t('OVERVIEW_REPORTS.HEATMAP.HOUR_LABEL', {
      hour: String(hour).padStart(2, '0'),
    }),
  }))
);

const QUANTILES = [0.2, 0.4, 0.6, 0.8, 0.9, 0.99];

const COLOR_SCHEMES = {
  blue: [
    'rgb(var(--blue-3))',
    'rgb(var(--blue-5))',
    'rgb(var(--blue-7))',
    'rgb(var(--blue-8))',
    'rgb(var(--blue-9))',
    'rgb(var(--blue-10))',
    'rgb(var(--blue-11))',
  ],
  green: [
    'rgb(var(--teal-3))',
    'rgb(var(--teal-5))',
    'rgb(var(--teal-7))',
    'rgb(var(--teal-8))',
    'rgb(var(--teal-9))',
    'rgb(var(--teal-10))',
    'rgb(var(--teal-11))',
  ],
};

const chartData = computed(() => {
  const groupedData = groupHeatmapByDay(props.heatmapData);
  const rows = Array.from(groupedData.entries()).map(([dateKey, rowData]) => {
    const valuesByHour = new Map(rowData.map(item => [item.hour, item]));
    const date = new Date(dateKey);

    return {
      id: dateKey,
      // Compact single-line label ("Sáb 12/09"); the full date stays in the tooltip.
      label: formatHeatmapRowLabel(date, locale.value),
      description: formatHeatmapRowDescription(date, locale.value),
      data: columns.value.map(({ id }) => valuesByHour.get(id) ?? null),
    };
  });

  return { columns: columns.value, rows };
});

const legendColors = computed(() => [
  'rgb(var(--solid-2))',
  ...COLOR_SCHEMES[props.colorScheme],
]);

const heatmapColors = computed(() => COLOR_SCHEMES[props.colorScheme]);

const colorSchemeClass = computed(() => {
  return props.colorScheme === 'green'
    ? '[--cw-viz-heatmap-focus-color:rgb(var(--teal-9))]'
    : '[--cw-viz-heatmap-focus-color:rgb(var(--blue-9))]';
});
</script>

<template>
  <div class="report-heatmap flex min-w-0 flex-col gap-3">
    <div
      v-if="isLoading"
      class="w-full overflow-x-auto"
      role="status"
      :aria-label="t('OVERVIEW_REPORTS.HEATMAP.LOADING')"
    >
      <div
        class="grid min-w-[46rem] gap-[5px]"
        :style="{
          gridTemplateColumns: `${ROW_LABEL_WIDTH}px repeat(24, minmax(24px, 1fr))`,
        }"
      >
        <template v-for="row in numberOfRows" :key="row">
          <div
            class="me-3 h-7 rounded bg-n-alpha-2 motion-safe:animate-pulse"
          />
          <div
            v-for="cell in 24"
            :key="cell"
            class="h-7 rounded-[3px] bg-n-alpha-2 motion-safe:animate-pulse"
          />
        </template>
        <div />
        <div
          v-for="column in columns"
          :key="column.id"
          class="flex h-5 items-end justify-center text-[10px] font-medium tabular-nums text-n-slate-11"
        >
          {{ column.label }}
        </div>
      </div>
    </div>
    <HeatmapChart
      v-else
      :data="chartData"
      :aria-label="ariaLabel"
      :colors="heatmapColors"
      :format-value="formatValue"
      :quantiles="QUANTILES"
      :cell-min-width="24"
      :cell-height="28"
      :gap="5"
      :row-label-width="ROW_LABEL_WIDTH"
      zero-color="rgb(var(--solid-2))"
      class="[--cw-viz-heatmap-cell-border-color:rgb(var(--border-weak))] [--cw-viz-heatmap-column-label-color:rgb(var(--slate-11))] [--cw-viz-heatmap-label-background:rgb(var(--solid-1))] [--cw-viz-heatmap-label-color:rgb(var(--slate-11))] [--cw-viz-heatmap-level-0-color:rgb(var(--solid-2))] [--cw-viz-heatmap-row-title-color:rgb(var(--slate-12))] [--cw-viz-heatmap-tooltip-background:rgb(var(--solid-2))] [--cw-viz-heatmap-tooltip-border-color:rgb(var(--border-strong))] [--cw-viz-heatmap-tooltip-color:rgb(var(--slate-12))] [--cw-viz-heatmap-tooltip-label-color:rgb(var(--slate-11))]"
      :class="colorSchemeClass"
    />
    <div
      class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-xs text-n-slate-11"
    >
      <span class="inline-flex items-center gap-1.5">
        <span
          class="i-hugeicons-information-circle size-3.5 shrink-0"
          aria-hidden="true"
        />
        {{ t('OVERVIEW_REPORTS.HEATMAP.LEGEND_HINT') }}
      </span>
      <div
        class="inline-flex items-center gap-2"
        data-test-id="heatmap-legend"
        role="img"
        :aria-label="t('OVERVIEW_REPORTS.HEATMAP.LEGEND_ARIA')"
      >
        <span>{{ t('OVERVIEW_REPORTS.HEATMAP.LESS') }}</span>
        <span class="flex items-center gap-[3px]" aria-hidden="true">
          <span
            v-for="color in legendColors"
            :key="color"
            class="size-3 rounded-[3px] border border-n-weak"
            :style="{ backgroundColor: color }"
          />
        </span>
        <span>{{ t('OVERVIEW_REPORTS.HEATMAP.MORE') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* One-line row labels: the full date is kept for the tooltip only. */
.report-heatmap :deep(.cw-viz-heatmap__row-label) {
  white-space: nowrap;
  line-height: 1.2;
}

.report-heatmap :deep(.cw-viz-heatmap__row-label span) {
  display: none;
}

.report-heatmap :deep(.cw-viz-heatmap__column-label) {
  white-space: nowrap;
}
</style>
